<template>
  <app-grid-item
    :item="signedDocument"
    :is-loading="isLoading"
    :is-selectable="hasOriginal"
    :is-selected="isSelected"
    :menu-items="menuItems"
    @update:isSelected="toggleSignedDocumentSelection(signedDocument)"
    item-type="signed-document"
  >
    <template #sub-header>
      <AppSignedDocumentsSignatureStatus :status="status" class="ml-auto" />
    </template>
    <template #details>
      <template v-if="displayedSignatoryDate">
        <app-grid-detail-item
          icon="calendar_today"
          :title="`${TRANSLATION_KEY_PREFIX}.signatoryDate`"
          data-locator="signature-date"
        >
          {{ displayedSignatoryDate }}
        </app-grid-detail-item>
      </template>
      <template v-if="displayedSignatoriesCount && hasOriginal">
        <app-grid-detail-item
          icon="person_edit"
          :title="`${TRANSLATION_KEY_PREFIX}.signatoriesCount`"
          data-locator="display-signatories-link"
        >
          <app-button
            type="text"
            size="sm"
            variant="ghost"
            @click="onViewSignatories"
          >
            {{ displayedSignatoriesCount }}
          </app-button>
        </app-grid-detail-item>
      </template>
    </template>
    <template #footer>
      <template v-if="hasOriginal">
        <div class="d-flex align-center column-gap-4 mt-4">
          <app-button
            class="d-flex flex-grow-1"
            data-locator="download-button"
            :disabled="isLoading || isPartOfSelection"
            fullWidth
            type="outlined"
            variant="neutral"
            @click="onDownloadFile"
          >
            {{ $t('common.download') }}
          </app-button>
          <AppTooltip
            :disabled="isLoading || isPartOfSelection"
          >
            <template #activator="{ props }">
              <app-button
                class="border"
                data-locator="share-button"
                :disabled="isLoading || isPartOfSelection"
                size="large"
                type="icon"
                v-bind="props"
                @click="onShare"
              >
                <app-material-icon name="share" />
              </app-button>
            </template>
            <span>{{ $t('common.share') }}</span>
          </AppTooltip>
        </div>
      </template>
      <template v-else>
        <app-alert-in-page
          data-locator="no-access-to-original-alert"
          :has-left-border="false"
          variant="warning"
          :dismissible="false"
        >
          {{ $t(`${TRANSLATION_KEY_PREFIX}.noAccessToOriginal`) }}
        </app-alert-in-page>
      </template>
    </template>
  </app-grid-item>
  <SignedDocumentsDownloadDialog
    v-if="isSingleFileDownloadDialogOpen"
    :signedDocument="signedDocument"
    @close="isSingleFileDownloadDialogOpen = false"
  />
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import AppSignedDocumentsSignatureStatus from '@/common/AppSignedDocumentsSignatureStatus.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { POLYOFFICE_MAX_EXPORT_SIZE } from '@/common/polyOffice/constants'
import { SignedEnvExport } from '@/common/polyOffice/polyOffice.model.js'
import { ISOToShortDate } from '@/common/utils/dates'
import { toast } from '@/common/utils/toast'
import SignedDocumentsDownloadDialog from '@/project/signed-documents/dialogs/SignedDocumentsDownloadDialog.vue'
import { statusDataMap } from '@/project/signed-documents/signedDocumentsDataMap'
import { ARCHIVES_ADD_ROUTE_NAME } from '@/router/routerData'
import { GET_EXPORT_IFRAME } from '@/store/modules/poly-office/action_types'
import { useArchivesStore } from '@/stores/archivesStore'

const TRANSLATION_KEY_PREFIX = 'project.signed-documents.SignedDocumentsGridItem'

export default {
  name: 'SignedDocumentsGridItem',
  components: {
    AppSignedDocumentsSignatureStatus,
    AppTooltip,
    SignedDocumentsDownloadDialog,
  },
  inject: [
    'isSignedDocumentSelected',
    'openDeleteOriginalsDialog',
    'openExportToIManageDialog',
    'openShareOriginalsPane',
    'openSignatoriesListDialog',
    'selectedSignedDocuments',
    'toggleSignedDocumentSelection',
  ],
  setup() {
    const archivesStore = useArchivesStore()
    return { archivesStore }
  },
  props: {
    isLoading: {
      type: Boolean,
    },
    signedDocument: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      isSingleFileDownloadDialogOpen: false,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapGetters('user', ['isArchivingEnabled', 'polyOfficeEnabled']),
    archiveAddRouteOptions () {
      return {
        params: {
          signedDocuments: [this.signedDocument],
        },
        query: {
          signedDocumentIds: this.signedDocument.id,
          fromMnemo: this.roomMnemo,
        },
      }
    },
    combinedSignedDocumentSize () {
      return this.signedDocument.signSize + this.signedDocument.proofSize
    },
    displayedSignatoriesCount() {
      return `${this.signatoriesWhoSigned}/${this.signatories}`
    },
    displayedSignatoryDate () {
      return ISOToShortDate(this.signedDocument.time)
    },
    hasOriginal () {
      return !!this.signedDocument.proofFileURL || !!this.signedDocument.signedDocumentURL
    },
    isUserSignatory () {
      return !!this.signedDocument.mySignature
    },
    isPartOfSelection () {
      return this.selectedSignedDocuments.length > 1 && this.isSelected
    },
    isSelected () {
      return this.isSignedDocumentSelected(this.signedDocument)
    },
    menuItems () {
      const menuItems = []

      menuItems.push({
        disabled: !this.hasOriginal,
        icon: 'download',
        id: 'download',
        title: this.$t(`common.download`),
        locatorValue: 'download-menu-item',
        action: () => this.onDownloadFile(),
      },
      {
        disabled: !this.hasOriginal,
        icon: 'share',
        id: 'share',
        title: this.$t(`common.share`),
        locatorValue: 'share-menu-item',
        action: () => this.onShare(),
      },
      {
        title: "separator",
      })

      menuItems.push({
        disabled: !this.hasOriginal,
        featureTag: this.isArchivingEnabled ? null : 'upgrade',
        icon: 'inventory_2',
        id: 'archive',
        title: this.$t(`common.archivesAdd`),
        locatorValue: 'archive-menu-item',
        action: () => this.onArchiveAdd(),
      })

      if (this.imanageEnabledAndLogged) {
        menuItems.push({
          disabled: !this.hasOriginal,
          id: 'export-to-imanage',
          iconComponent: 'app-icon-imanage',
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.exportToImanage`),
          locatorValue: 'imanage-export-menu-item',
          action: () => this.onExportToImanage(),
        })
      }

      if (this.polyOfficeEnabled) {
        menuItems.push({
          disabled: !this.hasOriginal || this.combinedSignedDocumentSize === null || this.combinedSignedDocumentSize > POLYOFFICE_MAX_EXPORT_SIZE,
          id: 'export-to-poly-office',
          iconComponent: 'app-icon-poly-office',
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.exportToPolyOffice`),
          locatorValue: 'poly-export-menu-item',
          action: () => this.onExportToPolyOffice(),
        })
      }

      menuItems.push(
      {
        title: "separator",
      },
      {
        icon: 'delete',
        iconColor: 'icon-danger',
        id: 'delete',
        style: 'text-text-danger',
        title: this.$t(`common.delete`),
        locatorValue: 'delete-menu-item',
        action: () => this.onDelete(),
      })

      return menuItems
    },
    signatories () {
      let counter = 0
      if (this.signedDocument.mySignature) counter++
      if (this.signedDocument.otherSignatures.length > 0) {
        counter += this.signedDocument.otherSignatures.length
      }
      return counter
    },
    signatoriesWhoSigned () {
      let counter = 0
      if (this.signedDocument.mySignature) counter++
      if (this.signedDocument.otherSignatures.length > 0) {
        counter += this.signedDocument.otherSignatures.length
      }
      return counter
    },
    status () {
      return statusDataMap(this.signedDocument)
    },
  },
  methods: {
    ...mapActions('polyOffice', [GET_EXPORT_IFRAME]),
    onArchiveAdd () {
      if (!this.isArchivingEnabled) {
        this.archivesStore.setDiscoverElectronicSafeDialogIsOpen(true)
      } else {
        this.$router.push({
          name: ARCHIVES_ADD_ROUTE_NAME,
          ...this.archiveAddRouteOptions,
        })
      }
    },
    onDelete () {
      this.openDeleteOriginalsDialog([this.signedDocument])
    },
    onDownloadFile () {
      this.isSingleFileDownloadDialogOpen = true
    },
    onExportToImanage () {
      this.openExportToIManageDialog([this.signedDocument.id])
    },
    onExportToPolyOffice () {
      this.polyOfficeOpenDialogHandler()
    },
    onShare () {
      this.openShareOriginalsPane([this.signedDocument])
    },
    onViewSignatories() {
      this.openSignatoriesListDialog(this.signedDocument)
    },
    async polyOfficeOpenDialogHandler () {
      const payload = new SignedEnvExport(
        this.roomMnemo,
        [this.signedDocument.id],
      )
      try {
        await this.GET_EXPORT_IFRAME({
          payload,
          successMessage: this.$t(`${TRANSLATION_KEY_PREFIX}.exportToPolyOfficeSuccess`),
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('common.polyOfficeErrors.ERR_FILE_TOO_LARGE', 1))
      }
    },
  },
}
</script>

<style scoped lang="scss">
.SignedDocumentsItemGrid {
  cursor: default;
  box-shadow: 0 0 23px 0 rgba(148, 148, 148, 0.2) !important;
  &:hover,
  &--selected {
    box-shadow: 0 0 23px 0 rgba(148, 148, 148, 0.2), 0 0 8px 0 rgba(0, 0, 0, 0.5) !important;
  }
}
.SignedDocumentsItemGrid-signatoriesLink {
  cursor: pointer;
  &:hover {
    background-color: #eee;
    color: rgb(var(--v-theme-primary));
    text-decoration: underline;
  }
}
.SignedDocumentsItemGrid-title {
  overflow: hidden;
  position: relative;
  line-height: 20px;
  max-height: 60px;
}
.SignedDocumentsItemGrid-headerIcon {
  font-size: 16px;
}

:deep(.app-button.border:disabled) {
  border-color: rgb(var(--v-theme-border-secondary)) !important;

  svg {
    opacity: 0.45 !important;
  }
}
</style>
